import { useEffect, useState } from 'react';
import { useNotify, useRedirect, useDataProvider } from 'react-admin';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    FormControlLabel,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { fetchKeyHolder } from './nodeClaimsApi';
import { describeArmConsequence } from './armConsequence';
import { isValidNodeId, nodeIdFromSerial, normalizeSerial, NODE_ID_LENGTH } from './normalize';
import type { NodeKeyHolder } from './types';

/** Matches NodeClaimAuthorization.DefaultExpirationDays on the server. */
const DEFAULT_EXPIRATION_DAYS = 30;

const EXPIRY_CHOICES = [1, 3, 7, 14, 30, 90];

/**
 * Arms a hardware serial to claim a NodeId.
 *
 * Written as a controlled form rather than react-admin's SimpleForm because the important
 * behaviour is not the fields: it is the live lookup of who currently holds the node's key,
 * and the consequence statement that lookup produces. See armConsequence.ts — the server does
 * not revoke on arm, so this screen must not imply that it does.
 */
export const NodeClaimArm = () => {
    const [hardwareSerial, setHardwareSerial] = useState('');
    const [expiresInDays, setExpiresInDays] = useState(DEFAULT_EXPIRATION_DAYS);
    const [reason, setReason] = useState('');
    const [acknowledged, setAcknowledged] = useState(false);
    const [holder, setHolder] = useState<NodeKeyHolder | null>(null);
    const [lookupError, setLookupError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    const notify = useNotify();
    const redirect = useRedirect();
    const dataProvider = useDataProvider();

    const normalizedSerial = normalizeSerial(hardwareSerial);
    // Derived, never typed: the node accepts no other ID (#1680).
    const normalizedNodeId = nodeIdFromSerial(hardwareSerial);
    const nodeIdValid = isValidNodeId(normalizedNodeId);
    const serialTooShort = normalizedSerial.length > 0 && normalizedSerial.length < NODE_ID_LENGTH;

    // Look the node up as soon as it is well formed. Debounced so typing a serial does not
    // fire a request per keystroke.
    useEffect(() => {
        if (!nodeIdValid) {
            setHolder(null);
            setLookupError(null);
            return;
        }

        let cancelled = false;
        const timer = setTimeout(() => {
            fetchKeyHolder(normalizedNodeId)
                .then(result => { if (!cancelled) { setHolder(result); setLookupError(null); } })
                .catch((e: Error) => { if (!cancelled) { setHolder(null); setLookupError(e.message); } });
        }, 300);

        return () => { cancelled = true; clearTimeout(timer); };
    }, [normalizedNodeId, nodeIdValid]);

    const consequence = describeArmConsequence(holder, hardwareSerial);

    // Reset the acknowledgement whenever the consequence could have changed, so a tick made
    // against one node cannot silently carry over to another.
    useEffect(() => { setAcknowledged(false); }, [normalizedNodeId, normalizedSerial]);

    const serialValid = normalizedSerial.length > 0 && normalizedSerial.length <= 128;
    const reasonValid = reason.trim().length > 0 && reason.trim().length <= 500;
    const acknowledgementSatisfied = !consequence?.blocked || acknowledged;
    const canSubmit = serialValid && nodeIdValid && reasonValid && acknowledgementSatisfied && !busy;

    const submit = async () => {
        setBusy(true);
        try {
            const expiresAt = new Date(Date.now() + expiresInDays * 24 * 60 * 60 * 1000).toISOString();
            await dataProvider.create('node-claims', {
                data: {
                    hardwareSerial: normalizedSerial,
                    nodeId: normalizedNodeId,
                    reason: reason.trim(),
                    expiresAt,
                },
            });
            notify(`Armed ${normalizedSerial} to claim ${normalizedNodeId}`, { type: 'success' });
            redirect('/node-claims');
        } catch (e) {
            notify(e instanceof Error ? e.message : 'Arming failed', { type: 'error' });
        } finally {
            setBusy(false);
        }
    };

    return (
        <Card sx={{ maxWidth: 760, margin: 'auto', mt: 2 }}>
            <CardContent>
                <Typography variant="h5" gutterBottom>Arm a node claim</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    The card carries no credential. This authorization is what lets a specific piece of
                    hardware claim a specific node, once, before its arming window closes.
                </Typography>

                <Stack spacing={3}>
                    <TextField
                        label="Hardware serial"
                        required
                        value={hardwareSerial}
                        onChange={e => setHardwareSerial(e.target.value)}
                        error={serialTooShort}
                        helperText={
                            serialTooShort
                                ? `A Pi serial is at least ${NODE_ID_LENGTH} characters. Check the serial before arming.`
                                : hardwareSerial && normalizedSerial !== hardwareSerial
                                    ? `Stored as ${normalizedSerial} — hyphens and spaces are removed.`
                                    : 'The Pi hardware serial. Hyphens and spaces are ignored.'
                        }
                        inputProps={{ style: { fontFamily: 'monospace' }, maxLength: 160 }}
                    />

                    <TextField
                        label="Node ID"
                        value={normalizedNodeId}
                        error={normalizedNodeId.length > 0 && !nodeIdValid}
                        helperText={
                            normalizedNodeId.length > 0 && !nodeIdValid
                                ? `A Node ID is exactly ${NODE_ID_LENGTH} letters or digits, so this serial cannot be armed.`
                                : 'Filled from the serial: its last 8 characters. The node accepts no other ID.'
                        }
                        inputProps={{ style: { fontFamily: 'monospace' }, readOnly: true }}
                    />

                    {lookupError && (
                        <Alert severity="warning">
                            <AlertTitle>Could not check this node's current key</AlertTitle>
                            {lookupError}. Arming is still possible, but the consequence below is unknown.
                        </Alert>
                    )}

                    {consequence && (
                        <Alert severity={consequence.severity}>
                            <AlertTitle>{consequence.title}</AlertTitle>
                            {consequence.detail}
                        </Alert>
                    )}

                    {consequence?.blocked && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={acknowledged}
                                    onChange={e => setAcknowledged(e.target.checked)}
                                />
                            }
                            label="I understand this hardware cannot claim until the existing key is revoked, and I am arming it anyway."
                        />
                    )}

                    <TextField
                        select
                        label="Arming window"
                        value={expiresInDays}
                        onChange={e => setExpiresInDays(Number(e.target.value))}
                        helperText="How long the hardware has to claim before the authorization lapses. Shorter is safer."
                    >
                        {EXPIRY_CHOICES.map(days => (
                            <MenuItem key={days} value={days}>
                                {days === 1 ? '1 day' : `${days} days`}
                                {days === DEFAULT_EXPIRATION_DAYS ? ' (default)' : ''}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        label="Reason"
                        required
                        multiline
                        minRows={2}
                        value={reason}
                        onChange={e => setReason(e.target.value)}
                        helperText="Recorded against your identity in the audit trail. Say why this node is being armed."
                        inputProps={{ maxLength: 500 }}
                    />

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button variant="contained" onClick={submit} disabled={!canSubmit}>
                            Arm
                        </Button>
                        <Button onClick={() => redirect('/node-claims')} disabled={busy}>
                            Cancel
                        </Button>
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
};
